import NavButtons from "../components/nav-buttons";
import { BeneficialOwnerData, Page } from "../types";
import KycBioInput from "../components/kyc-bio-input";
import { useEffect, useState } from "react";
import { DEFAULT_PERCENT_OWNERSHIP, STATES, TITLES } from "../const";
import { BeneficialOwnersButtons } from "../components/beneficial-owners-buttons";
import { VaultFormPage } from "@snap-mobile/vault";

type BeneficialOwnersPageProps = {
  visible: boolean;
  setCurrentPage: (page: Page | undefined) => void;
  onBeneficialOwnersValues: (values: BeneficialOwnerData[]) => void;
  onSubmit: () => Promise<void>;
};

export default function BeneficialOwnersPage({
  visible,
  setCurrentPage,
  onBeneficialOwnersValues,
  onSubmit,
}: BeneficialOwnersPageProps) {
  const [beneficialOwnersValues, setBeneficialOwnersValues] = useState<
    BeneficialOwnerData[]
  >([]);
  useEffect(() => {
    onBeneficialOwnersValues(beneficialOwnersValues);
  }, [beneficialOwnersValues, onBeneficialOwnersValues]);
  const addBeneficialOwner = () => {
    const highestBeneficialOwnerId = Math.max(
      ...beneficialOwnersValues.map((bo) => bo.id)
    );
    setBeneficialOwnersValues([
      ...beneficialOwnersValues,
      {
        id: highestBeneficialOwnerId + 1,
        state: STATES[0].value || "",
        title: TITLES[0].value || "",
        percentOwnership: DEFAULT_PERCENT_OWNERSHIP,
        valid: false,
      },
    ]);
  };
  const removeBeneficialOwner = (id: number) => {
    setBeneficialOwnersValues(
      beneficialOwnersValues.filter((bo) => bo.id !== id)
    );
  };
  const setBeneficialOwnerValue = ({
    value,
    key,
    id,
  }: {
    value: unknown;
    key: keyof BeneficialOwnerData;
    id: number;
  }) => {
    let beneficialOwner = beneficialOwnersValues.find((bo) => bo.id === id);
    if (!beneficialOwner) {
      throw new Error(`BeneficialOwner ${id} not present`);
    }
    beneficialOwner = { ...beneficialOwner, [key]: value };
    const newOwnersValues = [
      ...beneficialOwnersValues.filter((bo) => bo.id !== id),
      beneficialOwner,
    ];
    newOwnersValues.sort((a, b) => {
      if (a.id < b.id) {
        return -1;
      } else if (a.id > b.id) {
        return 1;
      }
      return 0;
    });
    setBeneficialOwnersValues(newOwnersValues);
  };
  return (
    <div
      style={{
        visibility: visible ? "visible" : "hidden",
        height: visible ? "auto" : 0,
        position: visible ? "static" : "absolute",
        top: visible ? "auto" : 0,
      }}
    >
      <h2 className="text-md lg:text-xl font-semibold">Beneficial Owners</h2>
      <p>
        Add the personal information of anyone other than yourself who owns 25%
        or more of the business.
      </p>
      {beneficialOwnersValues.length > 0 && (
        <VaultFormPage visible={visible}>
          {beneficialOwnersValues.map((beneficialOwnerValues, index) => (
            <div key={beneficialOwnerValues.id}>
              {beneficialOwnersValues.length > 1 && (
                <>
                  <hr className="my-2" />
                  Owner <span className="font-bold">#{index + 1}</span>
                </>
              )}
              <KycBioInput
                entityPrefix={`beneficialOwner${beneficialOwnerValues.id}`}
                onStateSelect={(state) =>
                  setBeneficialOwnerValue({
                    value: state,
                    key: "state",
                    id: beneficialOwnerValues.id,
                  })
                }
                onTitleSelect={(title) =>
                  setBeneficialOwnerValue({
                    value: title,
                    key: "title",
                    id: beneficialOwnerValues.id,
                  })
                }
                onValid={(isValid: boolean) =>
                  setBeneficialOwnerValue({
                    value: isValid,
                    key: "valid",
                    id: beneficialOwnerValues.id,
                  })
                }
                onKycPercentOwnershipChange={(value) =>
                  setBeneficialOwnerValue({
                    value,
                    key: "percentOwnership",
                    id: beneficialOwnerValues.id,
                  })
                }
                askPercentOwnership={true}
              />
              <BeneficialOwnersButtons
                onRemoveOwner={() =>
                  removeBeneficialOwner(beneficialOwnerValues.id)
                }
                onAddOwner={addBeneficialOwner}
              />
            </div>
          ))}
        </VaultFormPage>
      )}

      {beneficialOwnersValues.length === 0 && (
        <BeneficialOwnersButtons onAddOwner={addBeneficialOwner} />
      )}

      <NavButtons
        onBackClick={() => setCurrentPage("officer")}
        onSubmitClick={async () => {
          await onSubmit();
        }}
        submitButtonDisabled={
          beneficialOwnersValues.length > 0 &&
          beneficialOwnersValues.some((bo) => !bo.valid)
        }
      />
    </div>
  );
}
